import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell, Users, Sparkles, ExternalLink, Loader,
  ArrowRight, Inbox, ChevronRight,
} from 'lucide-react';
import { DigestPreviewDocket } from '../../lib/watchlistDigestService';
import { teamWorkspaceService } from '../../lib/teamWorkspaceService';
import { personalizedSummaryService, PersonalizedSummary } from '../../lib/personalizedSummaryService';
import { Workspace, WorkspaceSummary } from '../../types';

// ── Types ───────────────────────────────────────────────────────────────────

interface WorkspaceActivityItem {
  workspace: Workspace & { user_role: string; access_type: string };
  summary: WorkspaceSummary;
}

interface LibraryFeedSectionProps {
  digestDockets: DigestPreviewDocket[];
  digestLoading: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  const hours = Math.floor(ms / 3_600_000);
  const days = Math.floor(ms / 86_400_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Section shell ─────────────────────────────────────────────────────────────

interface FeedSectionProps {
  title: string;
  icon: React.ReactNode;
  linkTo?: string;
  linkLabel?: string;
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  children?: React.ReactNode;
}

const FeedSection: React.FC<FeedSectionProps> = ({
  title, icon, linkTo, linkLabel, loading, empty, emptyMessage, children,
}) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5">
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      </div>
      {linkTo && linkLabel && (
        <Link
          to={linkTo}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          {linkLabel}
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>

    {loading ? (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
        <Loader className="h-3.5 w-3.5 animate-spin" />
        Loading…
      </div>
    ) : empty ? (
      <p className="py-4 text-sm text-gray-400">{emptyMessage}</p>
    ) : (
      children
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

export const LibraryFeedSection: React.FC<LibraryFeedSectionProps> = ({
  digestDockets,
  digestLoading,
}) => {
  const navigate = useNavigate();

  const [workspaceActivity, setWorkspaceActivity] = useState<WorkspaceActivityItem[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);

  const [recentSummaries, setRecentSummaries] = useState<PersonalizedSummary[]>([]);
  const [summariesLoading, setSummariesLoading] = useState(true);

  const [feedError, setFeedError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Dockets on the watchlist that have new filings since last digest
  const activeDockets = digestDockets.filter(d => d.new_filings_count > 0);

  // Map of docket_number → new filing count (used for staleness badges)
  const stalenessMap = new Map<string, number>(
    activeDockets.map(d => [d.docket_number, d.new_filings_count])
  );

  useEffect(() => {
    setFeedError(false);
    setWorkspaceLoading(true);
    setSummariesLoading(true);
    // Workspace activity: fetch all workspaces → parallel summary fetches → sort by date
    const loadWorkspaceActivity = async () => {
      try {
        const workspaces = await teamWorkspaceService.getUserWorkspaces();
        if (workspaces.length === 0) { setWorkspaceActivity([]); return; }

        const results = await Promise.allSettled(
          workspaces.slice(0, 6).map(ws =>
            teamWorkspaceService
              .getWorkspaceSummaries(ws.id)
              .then(sums => sums.map(s => ({ workspace: ws, summary: s })))
              .catch(() => [] as WorkspaceActivityItem[])
          )
        );

        const all: WorkspaceActivityItem[] = results
          .filter((r): r is PromiseFulfilledResult<WorkspaceActivityItem[]> => r.status === 'fulfilled')
          .flatMap(r => r.value);

        all.sort(
          (a, b) => new Date(b.summary.shared_at).getTime() - new Date(a.summary.shared_at).getTime()
        );
        setWorkspaceActivity(all.slice(0, 5));
      } catch {
        setWorkspaceActivity([]);
        setFeedError(true);
      } finally {
        setWorkspaceLoading(false);
      }
    };

    // Recent summaries: get all, sort newest-first, take top 4
    const loadRecentSummaries = async () => {
      try {
        const all = await personalizedSummaryService.getAllUserPersonalizedSummaries();
        const sorted = [...all].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setRecentSummaries(sorted.slice(0, 4));
      } catch {
        setRecentSummaries([]);
        setFeedError(true);
      } finally {
        setSummariesLoading(false);
      }
    };

    loadWorkspaceActivity();
    loadRecentSummaries();
  }, [reloadKey]);

  const allLoaded = !digestLoading && !workspaceLoading && !summariesLoading;
  const allEmpty =
    allLoaded &&
    activeDockets.length === 0 &&
    workspaceActivity.length === 0 &&
    recentSummaries.length === 0;

  if (allEmpty && feedError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 mb-4">
          <Inbox className="h-7 w-7 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-700 mb-1">We couldn't load your feed</h3>
        <p className="text-sm text-gray-500 max-w-xs mb-5">
          Something went wrong loading your recent activity. This is usually temporary.
        </p>
        <button
          onClick={() => setReloadKey(k => k + 1)}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (allEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 mb-4">
          <Inbox className="h-7 w-7 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-700 mb-1">You're all caught up</h3>
        <p className="text-sm text-gray-500 max-w-xs mb-5">
          Follow dockets to get alerts on new filings, or generate a summary to see it here.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/watchlist')}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Follow dockets
          </button>
          <button
            onClick={() => navigate('/proceedings')}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
          >
            Browse proceedings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── 1. New filings on watched dockets ─────────────────────────── */}
      <FeedSection
        title="New filings on your dockets"
        icon={<Bell className="h-4 w-4 text-emerald-600" />}
        linkTo="/watchlist"
        linkLabel="Manage watchlist"
        loading={digestLoading}
        empty={activeDockets.length === 0}
        emptyMessage="No new filings since your last digest. You're up to date."
      >
        <div className="divide-y divide-gray-100">
          {activeDockets.map(docket => (
            <div
              key={docket.docket_number}
              className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <button
                  onClick={() =>
                    navigate(`/proceedings/${encodeURIComponent(docket.docket_number)}`)
                  }
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors text-left"
                >
                  {docket.docket_number}
                  <ExternalLink className="h-3 w-3 opacity-40" />
                </button>
                {docket.proceeding_name && (
                  <p className="mt-0.5 text-xs text-gray-500 line-clamp-1 max-w-md">
                    {docket.proceeding_name}
                  </p>
                )}
                {docket.top_filers.length > 0 && (
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    Top filers: {docket.top_filers.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                  +{docket.new_filings_count} filing{docket.new_filings_count !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() =>
                    navigate(`/proceedings/${encodeURIComponent(docket.docket_number)}`)
                  }
                  className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  Analyze
                </button>
              </div>
            </div>
          ))}
        </div>
      </FeedSection>

      {/* ── 2. Workspace activity ──────────────────────────────────────── */}
      <FeedSection
        title="Workspace activity"
        icon={<Users className="h-4 w-4 text-blue-600" />}
        linkTo="/workspaces"
        linkLabel="View all workspaces"
        loading={workspaceLoading}
        empty={workspaceActivity.length === 0}
        emptyMessage="No recent workspace activity. Share a summary with your team to get started."
      >
        <div className="divide-y divide-gray-100">
          {workspaceActivity.map(({ workspace, summary }) => (
            <div
              key={summary.shared_summary_id}
              className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-800">
                  <span className="font-medium">
                    {summary.shared_by_email.split('@')[0]}
                  </span>{' '}
                  shared a summary in{' '}
                  <button
                    onClick={() => navigate(`/workspace/${workspace.id}`)}
                    className="font-medium text-blue-700 hover:text-blue-800 underline underline-offset-2"
                  >
                    {workspace.name}
                  </button>
                </p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {workspace.docket_number}
                  {summary.summary_topics.length > 0 && (
                    <> · {summary.summary_topics.slice(0, 2).join(', ')}</>
                  )}
                  {' · '}
                  {formatRelativeDate(summary.shared_at)}
                </p>
              </div>
              <button
                onClick={() => navigate(`/workspace/${workspace.id}`)}
                className="shrink-0 rounded-lg border border-gray-200 bg-white p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </FeedSection>

      {/* ── 3. Recent summaries (with staleness badges) ───────────────── */}
      <FeedSection
        title="Your recent summaries"
        icon={<Sparkles className="h-4 w-4 text-orange-500" />}
        linkTo="/library?tab=summaries"
        linkLabel="View all summaries"
        loading={summariesLoading}
        empty={recentSummaries.length === 0}
        emptyMessage="No summaries yet. Analyze a proceeding to generate one."
      >
        <div className="divide-y divide-gray-100">
          {recentSummaries.map(summary => {
            const newFilings = stalenessMap.get(summary.docket_number) ?? 0;
            return (
              <div
                key={summary.id}
                className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() =>
                        navigate(`/proceedings/${encodeURIComponent(summary.docket_number)}`)
                      }
                      className="text-sm font-semibold text-gray-900 hover:text-orange-700 transition-colors"
                    >
                      {summary.docket_number}
                    </button>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 capitalize">
                      {summary.summary_type.replace('-', ' ')}
                    </span>
                    {newFilings > 0 && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        ⚠ {newFilings} new filing{newFilings !== 1 ? 's' : ''} since generated
                      </span>
                    )}
                  </div>
                  {summary.topics.length > 0 && (
                    <p className="mt-0.5 text-xs text-gray-500 line-clamp-1 max-w-sm">
                      {summary.topics.slice(0, 3).join(' · ')}
                    </p>
                  )}
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    {new Date(summary.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    {' · '}
                    {summary.document_count} doc{summary.document_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() =>
                    navigate(`/proceedings/${encodeURIComponent(summary.docket_number)}`)
                  }
                  className="shrink-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  View
                </button>
              </div>
            );
          })}
        </div>
      </FeedSection>
    </div>
  );
};
